import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ApiClient, TrackDto } from '../api';
import { createAudioEngine, type AudioEngine } from '../audio/engine';
import { useStoredState } from './useStoredState';

/** Refresh a signed URL this long before it expires, so a long set never stalls. */
const REFRESH_MARGIN_MS = 5 * 60_000;

export const SKIP_CHOICES = [10, 15, 30, 60] as const;
export type SkipSeconds = (typeof SKIP_CHOICES)[number];

export type PlayerState = {
  track: TrackDto | null;
  playing: boolean;
  time: number;
  duration: number;
  volume: number;
  muted: boolean;
  loading: boolean;
  error: string | null;
};

export type Player = PlayerState & {
  engine: AudioEngine | null;
  skipSeconds: SkipSeconds;
  setSkipSeconds(seconds: SkipSeconds): void;
  play(track: TrackDto): Promise<void>;
  toggle(): void;
  seekTo(seconds: number): void;
  seekToFraction(fraction: number): void;
  skip(delta: number): void;
  setVolume(volume: number): void;
  toggleMute(): void;
  close(): void;
};

export function usePlayer(api: ApiClient): Player {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const engineRef = useRef<AudioEngine | null>(null);
  const expiryRef = useRef<number>(0);
  // Guards against a refresh loop when a track is genuinely unplayable.
  const refreshingRef = useRef(false);

  // The engine is state as well as a ref: the ref is for imperative use inside
  // callbacks, the state is what makes the spectrum strip re-render once the graph
  // exists. Reading the ref during render would hand consumers a stale null.
  const [engine, setEngine] = useState<AudioEngine | null>(null);
  const [track, setTrack] = useState<TrackDto | null>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [volume, setStoredVolume] = useStoredState<number>('psc.volume', 1);
  const [muted, setMuted] = useStoredState<boolean>('psc.muted', false);
  const [skipSeconds, setSkipSeconds] = useStoredState<SkipSeconds>(
    'psc.skipSeconds',
    15,
    (value) => (SKIP_CHOICES.includes(value as SkipSeconds) ? (value as SkipSeconds) : 15),
  );

  /** The element is created once and reused: a media element can only ever be attached
   * to one Web Audio source node, so replacing it would silence the graph. */
  const getAudio = useCallback((): HTMLAudioElement => {
    if (!audioRef.current) {
      const element = new Audio();
      element.crossOrigin = 'anonymous';
      element.preload = 'metadata';
      audioRef.current = element;
    }
    return audioRef.current;
  }, []);

  const applyVolume = useCallback(
    (nextVolume: number, nextMuted: boolean) => {
      const engine = engineRef.current;
      const level = nextMuted ? 0 : Math.min(Math.max(nextVolume, 0), 1);
      if (engine) engine.setVolume(level);
      else if (audioRef.current) audioRef.current.volume = level;
    },
    [],
  );

  useEffect(() => {
    applyVolume(volume, muted);
  }, [applyVolume, muted, volume]);

  const loadSource = useCallback(
    async (target: TrackDto, resume: boolean) => {
      const audio = getAudio();
      const source = await api.playbackUrl(target.id);
      expiryRef.current = new Date(source.expiresAt).getTime();
      const previousTime = resume ? audio.currentTime : 0;
      audio.src = source.url;
      if (resume && previousTime > 0) {
        // Re-seek once the new source is ready; assigning src resets the position.
        const restore = () => {
          audio.currentTime = previousTime;
          audio.removeEventListener('loadedmetadata', restore);
        };
        audio.addEventListener('loadedmetadata', restore);
      }
    },
    [api, getAudio],
  );

  const play = useCallback(
    async (target: TrackDto) => {
      setError(null);
      setLoading(true);
      try {
        const audio = getAudio();
        if (track?.id !== target.id) {
          setTrack(target);
          setTime(0);
          setDuration(target.durationSeconds ?? 0);
          await loadSource(target, false);
        }
        if (!engineRef.current) {
          const created = createAudioEngine(audio);
          engineRef.current = created;
          setEngine(created);
          applyVolume(volume, muted);
        }
        await engineRef.current.resume();
        await audio.play();
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : 'Playback failed');
      } finally {
        setLoading(false);
      }
    },
    [applyVolume, getAudio, loadSource, muted, track, volume],
  );

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !track) return;
    if (audio.paused) {
      void engineRef.current?.resume();
      void audio.play().catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : 'Playback failed');
      });
    } else {
      audio.pause();
    }
  }, [track]);

  const seekTo = useCallback(
    (seconds: number) => {
      const audio = audioRef.current;
      if (!audio || !Number.isFinite(seconds)) return;
      const limit = audio.duration || duration || 0;
      const next = Math.min(Math.max(seconds, 0), limit);
      audio.currentTime = next;
      setTime(next);
    },
    [duration],
  );

  const seekToFraction = useCallback(
    (fraction: number) => {
      const limit = audioRef.current?.duration || duration || 0;
      seekTo(limit * Math.min(Math.max(fraction, 0), 1));
    },
    [duration, seekTo],
  );

  const skip = useCallback(
    (delta: number) => {
      const audio = audioRef.current;
      if (!audio) return;
      seekTo(audio.currentTime + delta);
    },
    [seekTo],
  );

  const close = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    setTrack(null);
    setPlaying(false);
    setTime(0);
    setDuration(0);
    setError(null);
  }, []);

  // Media element events drive React state rather than the other way around, so the UI
  // stays correct when playback changes for reasons we did not initiate.
  useEffect(() => {
    const audio = getAudio();
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onTime = () => setTime(audio.currentTime);
    const onMeta = () => {
      if (Number.isFinite(audio.duration)) setDuration(audio.duration);
    };
    const onEnded = () => setPlaying(false);
    const onError = () => {
      if (!track || refreshingRef.current) return;
      refreshingRef.current = true;
      // Most often an expired signature; re-signing is cheap, so try once.
      void loadSource(track, true)
        .then(() => audio.play())
        .catch(() => setError('Playback failed. Try again.'))
        .finally(() => {
          refreshingRef.current = false;
        });
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('durationchange', onMeta);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('durationchange', onMeta);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
  }, [getAudio, loadSource, track]);

  // Re-sign before the URL lapses so a two-and-a-half hour set plays straight through.
  useEffect(() => {
    if (!track) return;
    const delay = expiryRef.current - Date.now() - REFRESH_MARGIN_MS;
    if (!Number.isFinite(delay) || delay <= 0) return;
    const timer = setTimeout(() => {
      void loadSource(track, true).catch(() => undefined);
    }, delay);
    return () => clearTimeout(timer);
  }, [loadSource, track, time]);

  const setVolume = useCallback(
    (next: number) => {
      const clamped = Math.min(Math.max(next, 0), 1);
      setStoredVolume(clamped);
      if (clamped > 0 && muted) setMuted(false);
    },
    [muted, setMuted, setStoredVolume],
  );

  const toggleMute = useCallback(() => setMuted(!muted), [muted, setMuted]);

  return useMemo(
    () => ({
      track,
      playing,
      time,
      duration,
      volume,
      muted,
      loading,
      error,
      engine,
      skipSeconds,
      setSkipSeconds,
      play,
      toggle,
      seekTo,
      seekToFraction,
      skip,
      setVolume,
      toggleMute,
      close,
    }),
    [
      close,
      duration,
      engine,
      error,
      loading,
      muted,
      play,
      playing,
      seekTo,
      seekToFraction,
      setSkipSeconds,
      setVolume,
      skip,
      skipSeconds,
      time,
      toggle,
      toggleMute,
      track,
      volume,
    ],
  );
}
